import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminShell from "../components/AdminShell.jsx";
import AdminGalleryManager from "../components/AdminGalleryManager.jsx";
import { deleteProperty, listProperties } from "../admin/properties.js";
import { TEAM_MEMBERS } from "../teamMembers.js";
import "../admin.css";

function AdminDashboard() {
  const navigate = useNavigate();
  const [deletingPropertyId, setDeletingPropertyId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadProperties() {
      try {
        const nextProperties = await listProperties();

        if (isMounted) {
          setProperties(nextProperties);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || "We could not load the property list.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!properties.length) {
      setSelectedPropertyId("");
      return;
    }

    const hasSelectedProperty = properties.some(
      (property) => property.id === selectedPropertyId
    );

    if (!hasSelectedProperty) {
      setSelectedPropertyId(properties[0].id);
    }
  }, [properties, selectedPropertyId]);

  async function refreshProperties() {
    const nextProperties = await listProperties();
    setProperties(nextProperties);
    return nextProperties;
  }

  function handleOpenSelectedProperty() {
    if (!selectedPropertyId) {
      return;
    }

    navigate(`/admin/${encodeURIComponent(selectedPropertyId)}`);
  }

  async function handleDeleteProperty(property) {
    const shouldDelete = window.confirm(
      `Delete ${property.label}, all of its unit docs, and every photo in ${property.propertyStoragePath}?`
    );

    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingPropertyId(property.id);
      setError("");
      setStatus("");

      await deleteProperty(property.id);
      const nextProperties = await refreshProperties();

      setSelectedPropertyId(nextProperties[0]?.id || "");
      setStatus(`${property.label} was deleted.`);
    } catch (deleteError) {
      setError(deleteError.message || "We could not delete that property yet.");
    } finally {
      setDeletingPropertyId("");
    }
  }

  return (
    <AdminShell
      subtitle="Create property documents in Firebase under Properties, refresh this page, then choose the property here to manage its unit galleries and photos. Team headshots can be managed below too."
      title="Photo Management"
    >
      <section className="admin-card">
        <div className="admin-card__header admin-card__header--stacked">
          <div>
            <p className="admin-page__eyebrow">Property Picker</p>
            <h2>Open a Property Admin Page</h2>
          </div>
          <p>
            Any document you create in Firestore under <strong>Properties</strong> will appear here
            after a refresh.
          </p>
        </div>

        {loading ? (
          <div className="admin-gallery-empty">Loading properties...</div>
        ) : properties.length ? (
          <div className="admin-picker">
            <label className="admin-input-group">
              <span>Select Property</span>
              <select onChange={(event) => setSelectedPropertyId(event.target.value)} value={selectedPropertyId}>
                {properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="admin-card__actions admin-card__actions--compact">
              <button className="btn btn-primary btn-lg" onClick={handleOpenSelectedProperty} type="button">
                Open Property
              </button>
              <Link
                className="btn btn-outline-dark btn-lg"
                to={selectedPropertyId ? `/properties/${encodeURIComponent(selectedPropertyId)}` : "/"}
              >
                View Public Page
              </Link>
            </div>
          </div>
        ) : (
          <div className="admin-gallery-empty">
            Add a document in Firestore under <strong>Properties</strong>, then refresh this page.
          </div>
        )}

        {status ? <p className="admin-feedback admin-feedback--success">{status}</p> : null}
        {error ? <p className="admin-feedback admin-feedback--error">{error}</p> : null}
      </section>

      <section className="admin-card">
        <div className="admin-card__header admin-card__header--stacked">
          <div>
            <p className="admin-page__eyebrow">Properties</p>
            <h2>Manage Existing Properties</h2>
          </div>
          <p>
            Open a property to manage its homepage gallery and unit photos, or delete the property
            and its related storage folders from here.
          </p>
        </div>

        {loading ? (
          <div className="admin-gallery-empty">Loading properties...</div>
        ) : properties.length ? (
          <div className="admin-grid">
            {properties.map((property) => (
              <article className="admin-card admin-card--link" key={property.id}>
                <div className="admin-card__header admin-card__header--stacked">
                  <div>
                    <p className="admin-page__eyebrow">Property</p>
                    <h2>{property.label}</h2>
                  </div>
                  <p>{property.summary}</p>
                </div>
                <div className="admin-card__meta">
                  <span className="admin-badge">Firestore: {property.documentPath}</span>
                  <span className="admin-badge">Units: {property.units.length}</span>
                </div>
                <p className="admin-card__path">Storage Root: {property.propertyStoragePath}</p>
                <div className="admin-card__actions">
                  <Link className="btn btn-primary btn-lg" to={`/admin/${encodeURIComponent(property.id)}`}>
                    Manage {property.label}
                  </Link>
                  <Link
                    className="btn btn-outline-dark btn-lg"
                    to={`/properties/${encodeURIComponent(property.id)}`}
                  >
                    View Public Page
                  </Link>
                  <button
                    className="admin-delete-button"
                    disabled={deletingPropertyId === property.id}
                    onClick={() => handleDeleteProperty(property)}
                    type="button"
                  >
                    {deletingPropertyId === property.id ? "Deleting..." : "Delete Property"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="admin-gallery-empty">No properties found in Firestore.</div>
        )}
      </section>

      <section className="admin-card">
        <div className="admin-card__header admin-card__header--stacked">
          <div>
            <p className="admin-page__eyebrow">Team</p>
            <h2>Manage Team Photos</h2>
          </div>
          <p>
            Upload one photo for each team member. Uploading a new photo here replaces the old one
            on the homepage team section.
          </p>
          <p className="admin-upload-caution">
            Caution: upload a square headshot or it may crop awkwardly on the homepage. Team photos
            fit best around 1200x1200.
          </p>
        </div>

        <div className="admin-grid admin-grid--team">
          {TEAM_MEMBERS.map((member) => (
            <AdminGalleryManager
              description={`${member.role} photo for the homepage team section.`}
              documentPath={member.documentPath}
              emptyMessage="No team photo uploaded yet."
              enableCrop={false}
              heading={member.name}
              key={member.id}
              multiple={false}
              replaceExisting
              selectLabel="Select Photo"
              storageDirectory={member.storageDirectory}
              uploadCaution=""
              uploadButtonLabel="Save Photo"
            />
          ))}
        </div>
      </section>
    </AdminShell>
  );
}

export default AdminDashboard;
